import React, { useState, useRef, useCallback, DragEvent, memo } from "react";
import {
  Button,
  Drawer,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axiosClient from "../../api/AxiosClient";
import styles from "./chatwindow.module.css";

const MAX_FILES = 50;
const ACCEPTED_FILE_TYPES = [".pdf", ".doc", ".docx"];
const SUCCESS_MESSAGE_DURATION = 3000;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const validateFile = (file: File): boolean => {
  const extension = "." + file.name.split(".").pop()?.toLowerCase();
  return ACCEPTED_FILE_TYPES.includes(extension);
};

const ResumeUploadButton: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFiles = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    setError(null);
    const fileArray = Array.from(selectedFiles);
    const validFiles = fileArray.filter(validateFile);

    if (validFiles.length !== fileArray.length) {
      setError("Some files were skipped. Only PDF, DOC, and DOCX files are allowed.");
    }

    setFiles((prevFiles) => {
      const totalFiles = prevFiles.length + validFiles.length;
      if (totalFiles > MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files allowed. Please remove some files.`);
        return prevFiles;
      }

      // Filter out duplicates
      const newFiles = validFiles.filter(
        (newFile) =>
          !prevFiles.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size,
          ),
      );

      if (newFiles.length !== validFiles.length) {
        setError("Some duplicate files were skipped.");
      }

      return [...prevFiles, ...newFiles];
    });
  }, []);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  }, [handleFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  }, []);

  const handleClearAll = useCallback(() => {
    setFiles([]);
    setError(null);
    setSuccess(false);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      setError("Please select at least one resume to upload.");
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      await axiosClient.post("/stream/talent-scouting/upload-resumes", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(true);

      // Clear files after successful upload
      successTimeoutRef.current = setTimeout(() => {
        setFiles([]);
        setSuccess(false);
      }, SUCCESS_MESSAGE_DURATION);
    } catch (err: any) {
      console.error("Error uploading resumes:", err);
      setError(
        err.response?.data?.message ||
          "Failed to upload resumes. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  }, [files]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Box className={styles.learningPathButtonWrapper}>
        <Button
          variant="contained"
          onClick={handleDrawerOpen}
          startIcon={<UploadFileIcon />}
          className={styles.learningPathButton}
        >
          Upload Resumes
        </Button>
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500 },
            maxWidth: "100%",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: "1px solid #e5e7eb",
              background: "linear-gradient(135deg, #e8f5f3 0%, #d1ebe7 100%)",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#004141", fontSize: "18px" }}
            >
              Upload Resumes
            </Typography>
            <IconButton
              onClick={handleDrawerClose}
              size="small"
              sx={{ color: "#6b7280" }}
              aria-label="Close drawer"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {/* Info */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Upload up to {MAX_FILES} resume files (PDF, DOC, DOCX). These
                resumes will be indexed for talent scouting.
              </Typography>
            </Alert>

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Success */}
            {success && (
              <Alert
                severity="success"
                sx={{ mb: 3 }}
                icon={<CheckCircleIcon />}
              >
                Resumes uploaded successfully!
              </Alert>
            )}

            {/* Dropzone */}
            <Box
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="File upload area"
              sx={{
                border: "2px dashed #00d2aa",
                borderRadius: "12px",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#e8f5f3" : "#fafafa",
                transition: "all 0.2s ease",
                mb: 3,
                "&:hover": {
                  backgroundColor: "#e8f5f3",
                  borderColor: "#00b896",
                },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES.join(",")}
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                aria-label="File input"
              />
              <CloudUploadIcon
                sx={{ fontSize: 48, color: "#00a888", mb: 2 }}
              />
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#004141", mb: 1 }}
              >
                {isDragActive
                  ? "Drop files here..."
                  : "Drag & drop resume files here"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                or click to browse files
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#9ca3af", display: "block", mt: 1 }}
              >
                Supported formats: PDF, DOC, DOCX (Max {MAX_FILES} files)
              </Typography>
            </Box>

            {/* File List */}
            {files.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#004141" }}
                  >
                    Selected Files ({files.length}/{MAX_FILES})
                  </Typography>
                  {!uploading && (
                    <Button
                      size="small"
                      onClick={handleClearAll}
                      sx={{ color: "#dc3545", textTransform: "none" }}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>

                <List sx={{ bgcolor: "#fafafa", borderRadius: "8px" }}>
                  {files.map((file, index) => (
                    <ListItem
                      key={`${file.name}-${file.size}-${index}`}
                      sx={{
                        borderBottom:
                          index < files.length - 1
                            ? "1px solid #e5e7eb"
                            : "none",
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon sx={{ color: "#00a888" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={formatFileSize(file.size)}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#374151",
                            wordBreak: "break-word",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: "12px", color: "#9ca3af" },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFile(index)}
                          disabled={uploading}
                          size="small"
                          sx={{ color: "#dc3545" }}
                          aria-label={`Remove ${file.name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Upload Progress */}
            {uploading && (
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  p: 3,
                  bgcolor: "#f9fafb",
                  borderRadius: "8px",
                }}
              >
                <CircularProgress size={24} sx={{ color: "#00a888" }} />
                <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500 }}>
                  Uploading resumes...
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleDrawerClose}
              disabled={uploading}
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              sx={{
                backgroundColor: "#00d2aa",
                color: "#004141",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#00b896",
                },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#999",
                },
              }}
              startIcon={
                uploading ? (
                  <CircularProgress size={20} sx={{ color: "#004141" }} />
                ) : (
                  <UploadFileIcon />
                )
              }
            >
              {uploading ? "Uploading..." : "Upload Resumes"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default memo(ResumeUploadButton);
